// Cookieless analytics client.
//
// • Generates a per-day session id (sid) — a random opaque token kept in
//   sessionStorage, rotated automatically every UTC day.
// • Buffers events for ~1.5s and flushes them in a single POST.
// • Uses navigator.sendBeacon on pagehide so trailing events survive
//   navigation / tab close.
// • Fully no-op in development if REACT_APP_API_BASE is missing.

const BASE = process.env.REACT_APP_API_BASE || '';
const ENDPOINT = `${BASE}/api/track`;

const queue = [];
let flushTimer = null;
const FLUSH_MS = 1500;

function utcDayKey() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${d.getUTCMonth() + 1}-${d.getUTCDate()}`;
}

function getSid() {
  try {
    const raw = sessionStorage.getItem('mc_sid');
    if (raw) {
      const [day, sid] = raw.split('|');
      if (day === utcDayKey()) return sid;
    }
    const sid = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    sessionStorage.setItem('mc_sid', `${utcDayKey()}|${sid}`);
    return sid;
  } catch (_) {
    return 'no-storage';
  }
}

function schedule() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, FLUSH_MS);
}

function flush(useBeacon = false) {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  if (queue.length === 0) return;
  const batch = { events: queue.splice(0) };
  const body = JSON.stringify(batch);

  // Best-effort: sendBeacon survives navigation, fetch is the fallback.
  try {
    if (useBeacon && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
  } catch (_) { /* ignore */ }

  // Plain fetch — credentials so the user_id is captured if logged-in.
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    credentials: 'include',
    keepalive: true,
  }).catch(() => { /* swallow analytics errors */ });
}

export function track(event, props = {}) {
  if (!event) return;
  queue.push({
    event,
    ts: Date.now(),
    sid: getSid(),
    path: typeof window !== 'undefined' ? window.location.pathname : null,
    referrer: typeof document !== 'undefined' ? document.referrer : null,
    locale: (typeof navigator !== 'undefined' ? navigator.language : null) || null,
    props: props || {},
  });
  schedule();
}

export function trackPageview() {
  track('pageview');
}

// Hook these once at boot — they handle window-level events.
let attached = false;
export function attachAnalytics() {
  if (attached || typeof window === 'undefined') return;
  attached = true;
  window.addEventListener('pagehide', () => flush(true));
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
}
